import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CATEGORIES = [
  { value: 'academic', label: 'Academic Supplies' },
  { value: 'privilege', label: 'Privileges' },
  { value: 'experience', label: 'Experiences' },
  { value: 'general', label: 'General' },
];

const ICONS = [
  'Gift', 'Star', 'Trophy', 'Award', 'Crown', 'Sparkles',
  'BookOpen', 'Pencil', 'Palette', 'Music', 'Gamepad2', 'Coffee',
  'Pizza', 'IceCream', 'Ticket', 'Heart', 'Zap', 'Smile'
];

const RewardForm = ({ reward, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    starCost: 5,
    category: 'general',
    icon: 'Gift',
    isActive: true,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (reward) {
      setFormData({
        title: reward?.title || '',
        description: reward?.description || '',
        starCost: reward?.starCost || 5,
        category: reward?.category || 'general',
        icon: reward?.icon || 'Gift',
        isActive: reward?.isActive !== undefined ? reward?.isActive : true,
      });
    }
  }, [reward]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData?.title?.trim()) return;
    setSubmitting(true);
    await onSubmit(formData);
    setSubmitting(false);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-border p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-semibold text-foreground">
          {reward ? 'Edit Reward' : 'Create New Reward'}
        </h3>
        <button onClick={onCancel} className="p-1 hover:bg-gray-100 rounded-lg">
          <Icon name="X" size={18} className="text-muted-foreground" />
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="reward-title" className="block text-sm font-medium text-foreground mb-1">Title *</label>
            <input
              type="text"
              id="reward-title"
              name="title"
              autoComplete="off"
              value={formData?.title}
              onChange={e => setFormData({ ...formData, title: e?.target?.value })}
              placeholder="e.g., Extra Break Time"
              required
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label htmlFor="reward-starCost" className="block text-sm font-medium text-foreground mb-1">Star Cost *</label>
            <input
              type="number"
              id="reward-starCost"
              name="starCost"
              autoComplete="off"
              value={formData?.starCost}
              onChange={e => setFormData({ ...formData, starCost: parseInt(e?.target?.value) || 1 })}
              min="1"
              max="100"
              required
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        <div>
          <label htmlFor="reward-description" className="block text-sm font-medium text-foreground mb-1">Description</label>
          <textarea
            id="reward-description"
            name="description"
            value={formData?.description}
            onChange={e => setFormData({ ...formData, description: e?.target?.value })}
            placeholder="Describe what this reward includes..."
            rows={2}
            className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="reward-category" className="block text-sm font-medium text-foreground mb-1">Category</label>
            <select
              id="reward-category"
              name="category"
              value={formData?.category}
              onChange={e => setFormData({ ...formData, category: e?.target?.value })}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {CATEGORIES?.map(cat => (
                <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Icon</label>
            <div className="flex flex-wrap gap-2 p-2 border border-border rounded-lg max-h-24 overflow-y-auto">
              {ICONS?.map(iconName => (
                <button
                  key={iconName}
                  type="button"
                  onClick={() => setFormData({ ...formData, icon: iconName })}
                  className={`p-1.5 rounded-lg transition-colors ${
                    formData?.icon === iconName
                      ? 'bg-primary text-white' :'hover:bg-gray-100 text-muted-foreground'
                  }`}
                  title={iconName}
                >
                  <Icon name={iconName} size={16} />
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="isActive"
            checked={formData?.isActive}
            onChange={e => setFormData({ ...formData, isActive: e?.target?.checked })}
            className="rounded"
          />
          <label htmlFor="isActive" className="text-sm text-foreground">Active (visible to students)</label>
        </div>

        <div className="flex justify-end space-x-3 pt-2">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={submitting} iconName={submitting ? undefined : 'Save'}>
            {submitting ? 'Saving...' : (reward ? 'Update Reward' : 'Create Reward')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default RewardForm;
